import { RecalledValue } from "./RecalledValue";
import { RecallType } from "./RecallType";
import { MentionModel } from "../models/MentionModel";
import { isNullOrEmpty, isNullOrNaN } from "../utils";
import { QuestionType, isImage } from "../models/enums/QuestionType";
import { VariableType } from "../models/enums/VariableType";
import { QuestionModel } from "../models/QuestionModel";
import { ChoiceRecalledValue } from "./ChoiceRecalledValue";
import { VariableModel } from "../models/VariableModel";
import { QuestionnaireModel } from "../models/QuestionnaireModel";
import {
    getVariableWithName,
    getQuestionWithVariable,
} from "../models/utilities/questionnaireUtilities";
import { translate } from "../models/utilities/translationUtilities";
import { getNumeral, formatNumeric } from "../utils/Number";
import { getSettingNumber } from "../models/utilities/settingUtilities";
import { shouldAutoFormat } from "../models/utilities/questionUtilities";
import { getChoice } from "../models/utilities/variableUtilities";

export class AnswerRecalledValue extends RecalledValue {
    mentions: MentionModel[];
    language: string;
    questionnaire: QuestionnaireModel;
    question: QuestionModel;
    variable: VariableModel;

    constructor(
        mentions: MentionModel[],
        language: string,
        questionnaire: QuestionnaireModel,
        variableName: string,
    ) {
        super();
        this.mentions = mentions;
        this.language = language;
        this.questionnaire = questionnaire;
        this.question = getQuestionWithVariable(questionnaire, variableName);
        this.variable = getVariableWithName(questionnaire, variableName);
    }

    evaluateProperty(
        mode: RecallType,
        property: string,
        parameters: string[],
    ): string | RecalledValue {
        switch (property) {
            case "image":
                if (this.question != null && isImage(this.question.type)) return this.toImage();
                return "";
            case "answer":
            case "answers": {
                if (parameters != null && parameters.length > 0) {
                    const mention = this.mentions[Number.parseInt(parameters[0], 10) - 1];
                    this.mentions = [mention];
                }
                return this;
            }
            case "choice":
            case "choices":
                if (parameters.length > 0 && this.variable && this.variable.choiceList) {
                    const choice = getChoice(this.variable, parameters[0]);
                    if (choice)
                        return new ChoiceRecalledValue(
                            this.question,
                            this.language,
                            this.questionnaire,
                            choice,
                            this.mentions.find((m) => m.value === choice.value),
                        );
                }
                return "";
            case "choicebyindex":
                {
                    let cIndex;
                    if (
                        parameters.length > 0 &&
                        this.variable &&
                        this.variable.choiceList &&
                        !isNaN((cIndex = Number(parameters[0]))) &&
                        cIndex > 0 &&
                        this.variable.choiceList.length >= cIndex
                    ) {
                        const choice = this.variable.choiceList[cIndex - 1];
                        return translate(choice, "TEXT", this.language);
                    }
                }
                break;
            case "count":
                return this.mentions.length.toString();
            case "openend": {
                const mention = this.mentions.find((m) => !isNullOrEmpty(m.openEnd));
                if (mention != null) return mention.openEnd;
                return "";
            }
            case "code":
                return this.toCode();
            case "text":
                return this.toText();
            case "textlabel":
                if (this.question?.translatedTexts?.text) {
                    return translate(this.question, "TEXT", this.language);
                }
            default:
                return this;
        }

        return this;
    }

    toImage(): string {
        const mention = this.mentions[0];
        if (mention == null) return "";
        if (this.question == null) return mention.value;
        const answer = getChoice(this.variable, mention.value);
        return `<img src="${translate(answer, "ImageUrl", this.language)}" />`;
    }

    toCode(mentionId = 0): string {
        const mention = this.mentions[mentionId];
        return mention === undefined || isNullOrEmpty(mention.value) ? "0" : mention.value;
    }

    toText(): string {
        const { question, mentions, variable } = this;
        const mention = mentions[0];
        if (!mention) return "";
        if (question && question.type === QuestionType.OpenEndAnswer) {
            const openEndMention = mentions.find((m) => m.openEnd !== undefined);
            if (openEndMention) return openEndMention.openEnd;
        }
        if (!isNullOrEmpty(mention.value)) {
            if (variable && variable.type !== VariableType.Computed) {
                const choice = getChoice(variable, mention.value);
                if (choice) return translate(choice, "TEXT", this.language);
            }
            if (question && question.type === QuestionType.NumericAnswer) {
                const num = getNumeral(mention.value).value();
                if (!isNullOrNaN(num))
                    return formatNumeric(
                        num,
                        getSettingNumber(question, "NumberOfDecimals"),
                        this.language,
                        false,
                        shouldAutoFormat(question),
                    );
            }
        }
        return mention.value;
    }
}
